import { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Award,
  FileText,
  Wrench,
  CalendarDays,
  Briefcase,
  Plane,
  Mail,
  Phone,
  MapPin,
  Plus,
  Loader2,
  Download,
  ListChecks,
  BarChart3,
  Star,
  CheckCircle2,
  SkipForward,
  Package,
  Pencil,
  Shield,
  Trash2,
} from 'lucide-react';
import { AddTravelDialog } from '@/app/pages/crew/travel/add-travel-dialog';
import { PermissionsTab } from '@/app/pages/crew/techs/permissions-tab';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/app/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  useTechProfile,
  useTechCertificationsForTech,
  useTechDocuments,
  useTechSkills,
  useTechAvailability,
  useAssignments,
  useTravelRecords,
  useUpcomingTravel,
  useCreateCertificationForTech,
  useUpdateTechCertification,
  useRenewTechCertification,
  useCreateTechSkill,
  useUploadTechDocument,
  useTechOnboarding,
  useCompleteOnboardingItem,
  useSkipOnboardingItem,
  usePerformanceSummary,
  useCreatePerformanceReview,
  useUpdateTechProfile,
} from '@/hooks/use-crew';
import { useFiledDocuments } from '@/hooks/use-filed-documents';
import { Switch } from '@/app/components/ui/switch';
import { usePermissions } from '@/hooks/use-permissions';
import { usePlatformModules } from '@/hooks/use-modules';
import { useTechPayRates } from '@/hooks/use-payroll';
import {
  useOrganizationSettings,
  useOrgCurrency,
  useOrgTimeZone,
  useOrgToday,
} from '@/hooks/use-settings';
import { useAuth } from '@/contexts/auth-context';
import { OverrideMarker } from '@/components/crew/override-marker';
import { toast } from '@/app/components/ui/simple-toaster';
import { useTechProjectsData, useTechSafetyData } from '@/hooks/use-platform';
import { useCreateJobPacket } from '@/hooks/use-job-packets';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, resolveMediaUrl, handleApiError } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import { addLocalDays, formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import { formatMoney } from '@/lib/currency';
import { CertificationHealthBadge } from '@/app/pages/crew/certification-health';
import type {
  TechProfile,
  TechStatus,
  TechCertStatus,
  TechCertType,
  SkillProficiency,
  AvailabilityStatus,
  AssignmentStatus,
  TravelType,
  TechCertification,
  TechDocument,
  TechSkill,
  AvailabilityEntry,
  CrewAssignment,
  TravelRecord,
  OnboardingItemStatus,
  OnboardingItemType,
  FiledDocument,
} from '@/lib/types';

const statusColors: Record<TechStatus, string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-800',
  on_leave: 'bg-yellow-100 text-yellow-800',
  terminated: 'bg-red-100 text-red-800',
  left: 'bg-red-100 text-red-800',
};

const statusLabels: Record<TechStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  on_leave: 'On Leave',
  terminated: 'Terminated',
  left: 'Left',
};

const certStatusColors: Record<TechCertStatus, string> = {
  valid: 'bg-green-100 text-green-800',
  expiring_soon: 'bg-yellow-100 text-yellow-800',
  expired: 'bg-red-100 text-red-800',
  revoked: 'bg-gray-100 text-gray-800',
};

const proficiencyColors: Record<SkillProficiency, string> = {
  beginner: 'bg-gray-100 text-gray-800',
  intermediate: 'bg-blue-100 text-blue-800',
  advanced: 'bg-purple-100 text-purple-800',
  expert: 'bg-green-100 text-green-800',
};

const availabilityColors: Record<AvailabilityStatus, string> = {
  available: 'bg-green-100 text-green-800',
  unavailable: 'bg-red-100 text-red-800',
  tentative: 'bg-yellow-100 text-yellow-800',
  on_project: 'bg-blue-100 text-blue-800',
  unknown: 'bg-gray-100 text-gray-600',
};

const assignmentStatusColors: Record<AssignmentStatus, string> = {
  scheduled: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  completed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800',
};

const travelTypeLabels: Record<TravelType, string> = {
  flight: 'Flight',
  car_rental: 'Car Rental',
  hotel: 'Hotel',
  other: 'Other',
};

type TabKey = 'overview' | 'certifications' | 'documents' | 'skills' | 'availability' | 'assignments' | 'travel' | 'projects' | 'safety_records' | 'onboarding' | 'performance' | 'permissions';

const tabs: { key: TabKey; label: string; icon: React.ElementType }[] = [
  { key: 'overview', label: 'Overview', icon: User },
  { key: 'certifications', label: 'Certifications', icon: Award },
  { key: 'documents', label: 'Documents', icon: FileText },
  { key: 'skills', label: 'Skills', icon: Wrench },
  { key: 'availability', label: 'Availability', icon: CalendarDays },
  { key: 'assignments', label: 'Assignments', icon: Briefcase },
  { key: 'projects', label: 'Projects', icon: Briefcase },
  { key: 'safety_records', label: 'Safety Records', icon: FileText },
  { key: 'travel', label: 'Travel', icon: Plane },
  { key: 'onboarding', label: 'Onboarding', icon: ListChecks },
  { key: 'performance', label: 'Performance', icon: BarChart3 },
  { key: 'permissions', label: 'Permissions', icon: Shield },
];

export function TechDetailPage({ basePath = '/crew/techs' }: { basePath?: string } = {}) {
  const { id } = useParams<{ id: string }>();
  const techId = Number(id);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const { isAdmin } = usePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);

  const { data: tech, isLoading, error } = useTechProfile(techId);

  // Soft delete via the existing DELETE endpoint (H3PAY-P12). The person,
  // user account and pay history are kept; only the crew profile goes.
  const removeTechProfile = useMutation({
    mutationFn: async () => {
      await apiClient.delete(`/api/v1/crew/techs/${techId}/`);
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: queryKeys.techProfiles.detail(techId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.techProfiles.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.persons.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.staffingBoard.all });
      toast.success('Tech profile removed');
      navigate(basePath);
    },
    onError: (err) => {
      toast.error(handleApiError(err).message);
    },
  });

  const visibleTabs = useMemo(
    () =>
      tabs.filter(
        (tab) => tab.key !== 'permissions' || (isAdmin && !!tech?.user)
      ),
    [isAdmin, tech?.user]
  );

  useEffect(() => {
    const visibleKeys = new Set(visibleTabs.map((tab) => tab.key));
    if (!visibleKeys.has(activeTab)) {
      setActiveTab('overview');
    }
  }, [activeTab, visibleTabs]);

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (error || !tech) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="text-center py-12">
          <p className="text-gray-600">Technician not found</p>
          <Button asChild variant="outline" className="mt-4">
            <Link to={basePath}>Back to Technicians</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-start gap-4">
        <Button asChild variant="ghost" size="icon" className="mt-1">
          <Link to={basePath}>
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-3">
              {tech.photo ? (
                <img
                  src={tech.photo}
                  alt={tech.full_name}
                  className="h-14 w-14 rounded-full object-cover"
                />
              ) : (
                <div className="h-14 w-14 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-blue-600 font-bold text-xl">
                    {tech.first_name[0]}{tech.last_name[0]}
                  </span>
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{tech.full_name}</h1>
                {tech.job_title && (
                  <p className="text-gray-600">{tech.job_title}</p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 self-start sm:ml-2">
              <Badge className={statusColors[tech.status]}>
                Employment: {statusLabels[tech.status]}
              </Badge>
              <CertificationHealthBadge certifications={tech.certifications} />
            </div>
          </div>
        </div>
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            className="mt-1 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            onClick={() => setShowRemoveDialog(true)}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Remove tech profile
          </Button>
        )}
      </div>

      {isAdmin && (
        <AlertDialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove tech profile</AlertDialogTitle>
              <AlertDialogDescription>
                Remove the tech profile for {tech.full_name}? They will no longer appear in
                Crew → Techs or on the H3ightboard bench; any active board placements stay
                visible until they are cleared. Their person record, user account and pay
                history are kept.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(event) => {
                  event.preventDefault();
                  removeTechProfile.mutate();
                }}
                disabled={removeTechProfile.isPending}
                className="bg-red-600 hover:bg-red-700"
              >
                {removeTechProfile.isPending ? 'Removing...' : 'Remove tech profile'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      <div className="border-b overflow-x-auto">
        <div className="flex gap-0 min-w-max">
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        {activeTab === 'overview' && <OverviewTab tech={tech} />}
        {activeTab === 'certifications' && <CertificationsTab techId={techId} />}
        {activeTab === 'documents' && <DocumentsTab techId={techId} personId={tech.person} />}
        {activeTab === 'skills' && <SkillsTab techId={techId} />}
        {activeTab === 'availability' && <AvailabilityTab techId={techId} />}
        {activeTab === 'assignments' && <AssignmentsTab techId={techId} />}
        {activeTab === 'projects' && <TechProjectsTab techId={techId} />}
        {activeTab === 'safety_records' && <TechSafetyRecordsTab techId={techId} />}
        {activeTab === 'travel' && <TravelTab techId={techId} />}
        {activeTab === 'onboarding' && <OnboardingTab techId={techId} tech={tech} />}
        {activeTab === 'performance' && <PerformanceTab techId={techId} />}
        {activeTab === 'permissions' && isAdmin && (
          tech.user ? <PermissionsTab userId={tech.user} /> : (
            <div className="text-center py-12 text-gray-600">
              This person does not have a user account.
            </div>
          )
        )}
      </div>
    </div>
  );
}

function CurrentPayRateCard({ tech }: { tech: TechProfile }) {
  const permissions = usePermissions();
  const { isModuleEnabled } = usePlatformModules();
  const canSeeRates =
    isModuleEnabled('payroll') &&
    (permissions.isAdmin || permissions.hasMinModuleRole('payroll', 'manager'));
  if (!canSeeRates) return null;
  return <CurrentPayRateBody tech={tech} />;
}

function CurrentPayRateBody({ tech }: { tech: TechProfile }) {
  const orgCurrency = useOrgCurrency();
  const orgToday = useOrgToday();
  const { data: rates = [], isLoading } = useTechPayRates({
    person: tech.person,
    project: 'none',
  });

  const current = useMemo(
    () =>
      [...rates]
        .filter((r) => r.project == null && r.effective_date <= orgToday)
        .sort((a, b) => b.effective_date.localeCompare(a.effective_date))[0],
    [rates, orgToday],
  );

  const rows = current
    ? [
        { label: 'Hourly Rate', value: current.hourly_rate },
        { label: 'Daily Rate', value: current.daily_rate },
        { label: 'Overtime Rate', value: current.overtime_rate },
        { label: 'Per Diem', value: current.per_diem_rate },
      ].filter((row): row is { label: string; value: string } => !!row.value)
    : [];

  return (
    <Card data-testid="current-pay-rate">
      <CardHeader>
        <CardTitle>Pay Rate</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
        ) : current ? (
          <>
            {rows.map((row) => (
              <div key={row.label} className="flex justify-between">
                <span className="text-sm text-gray-600">{row.label}</span>
                <span className="text-sm font-medium">{formatMoney(row.value, orgCurrency)}</span>
              </div>
            ))}
            <p className="text-xs text-gray-500">
              Effective since {formatDateOnly(current.effective_date)}
            </p>
          </>
        ) : (
          <p className="text-sm text-gray-500">No pay rate set yet.</p>
        )}
        <Link to="/payroll/rates" className="text-sm text-blue-600 hover:underline">
          Manage in Pay → Pay Rates
        </Link>
      </CardContent>
    </Card>
  );
}

function OverviewTab({ tech }: { tech: NonNullable<ReturnType<typeof useTechProfile>['data']> }) {
  const permissions = usePermissions();
  const { canManageUsers } = permissions;
  const updateTechProfile = useUpdateTechProfile();
  const { data: orgSettings } = useOrganizationSettings();

  const icMinRole = orgSettings?.inspection_certified_min_role || 'manager';
  const canToggleIC = permissions.hasMinRole(icMinRole === 'manager' ? 'Manager' : 'Supervisor');

  const handleToggleIC = async (checked: boolean) => {
    try {
      await updateTechProfile.mutateAsync({ id: tech.id, data: { inspection_certified: checked } });
      toast.success(checked ? 'Inspection Certified Enabled' : 'Inspection Certified Disabled');
    } catch (_err) {
      toast.error('Failed to update Inspection Certified status');
    }
  };
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: tech.first_name,
    last_name: tech.last_name,
    email: tech.email,
    phone: tech.phone || '',
    job_title: tech.job_title || '',
    employment_type: tech.employment_type,
    status: tech.status,
  });
  const [editError, setEditError] = useState<string | null>(null);
  const [confirmAdminRestoreOpen, setConfirmAdminRestoreOpen] = useState(false);

  const handleEditSave = async (confirmAdminGrant = false) => {
    setEditError(null);
    try {
      const updated = await updateTechProfile.mutateAsync({
        id: tech.id,
        data: confirmAdminGrant ? { ...editForm, confirm_admin_grant: true } : editForm,
      });
      setIsEditing(false);
      if (updated.reactivation?.warnings.length) {
        updated.reactivation.warnings.forEach((w) => toast.warning(w));
      } else if (updated.reactivation) {
        toast.success('Access restored', {
          description: 'Login, company membership and module roles are back.',
        });
      } else {
        toast.success('Profile Updated', { description: 'Employee profile has been updated.' });
      }
    } catch (err) {
      const apiError = handleApiError(err);
      if (apiError.code === 'confirm_admin_grant_required' && !confirmAdminGrant) {
        setConfirmAdminRestoreOpen(true);
        return;
      }
      setEditError(apiError.message);
    }
  };

  if (isEditing) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Edit Profile</h3>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={() => handleEditSave()} disabled={updateTechProfile.isPending}>
              {updateTechProfile.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </div>
        <AlertDialog open={confirmAdminRestoreOpen} onOpenChange={setConfirmAdminRestoreOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Restore Admin access?</AlertDialogTitle>
              <AlertDialogDescription>
                This person was an Admin. Reactivating them gives back full Admin access to this
                company.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setConfirmAdminRestoreOpen(false);
                  void handleEditSave(true);
                }}
              >
                Restore Admin access
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {editError && (
          <div className="bg-red-50 text-red-700 text-sm p-3 rounded-md border border-red-200">{editError}</div>
        )}
        <Card>
          <CardHeader><CardTitle>Personal Information</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit_first_name">First Name</Label>
                <Input id="edit_first_name" value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="edit_last_name">Last Name</Label>
                <Input id="edit_last_name" value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} />
              </div>
            </div>
            <div>
              <Label htmlFor="edit_email">Email</Label>
              <Input id="edit_email" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="edit_phone">Phone</Label>
              <Input id="edit_phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Employment Details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="edit_job_title">Job Title</Label>
              <Input id="edit_job_title" value={editForm.job_title} onChange={(e) => setEditForm({ ...editForm, job_title: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="edit_employment_type">Employment Type</Label>
              <Select value={editForm.employment_type} onValueChange={(v) => setEditForm({ ...editForm, employment_type: v as typeof tech.employment_type })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_time">Full Time</SelectItem>
                  <SelectItem value="part_time">Part Time</SelectItem>
                  <SelectItem value="contractor">Contractor</SelectItem>
                  <SelectItem value="subcontractor">Subcontractor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit_status">Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v as typeof tech.status })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="on_leave">On Leave</SelectItem>
                  <SelectItem value="terminated">Terminated</SelectItem>
                  <SelectItem value="left">Left</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {canManageUsers && (
        <div className="lg:col-span-2 flex justify-end">
          <Button variant="outline" size="sm" onClick={() => {
            setEditForm({
              first_name: tech.first_name,
              last_name: tech.last_name,
              email: tech.email,
              phone: tech.phone || '',
              job_title: tech.job_title || '',
              employment_type: tech.employment_type,
              status: tech.status,
            });
            setIsEditing(true);
          }}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit Profile
          </Button>
        </div>
      )}
      <Card>
        <CardHeader>
          <CardTitle>Personal Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <InfoRow icon={Mail} label="Email" value={tech.email} />
          <InfoRow icon={Phone} label="Phone" value={tech.phone} />
          {tech.secondary_phone && (
            <InfoRow icon={Phone} label="Secondary Phone" value={tech.secondary_phone} />
          )}
          {tech.date_of_birth && (
            <InfoRow icon={CalendarDays} label="Date of Birth" value={tech.date_of_birth} />
          )}
          {(tech.address || tech.city) && (
            <InfoRow
              icon={MapPin}
              label="Address"
              value={[tech.address, tech.city, tech.state_province, tech.country, tech.postal_code]
                .filter(Boolean)
                .join(', ')}
            />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Employment Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Employment Type</span>
            <span className="text-sm font-medium">
              {tech.employment_type.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            </span>
          </div>
          {tech.hire_date && (
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Hire Date</span>
              <span className="text-sm font-medium">{tech.hire_date}</span>
            </div>
          )}
          <div className="flex items-center justify-between pt-2 border-t">
            <div className="space-y-0.5">
              <span className="text-sm font-medium">Inspection Certified</span>
              <p className="text-xs text-gray-500">
                {tech.inspection_certified
                  ? 'This employee can perform and submit inspections'
                  : 'Enable to allow this employee to perform inspections'}
              </p>
            </div>
            <Switch
              checked={tech.inspection_certified ?? false}
              onCheckedChange={handleToggleIC}
              disabled={!canToggleIC || updateTechProfile.isPending}
            />
          </div>
        </CardContent>
      </Card>

      <CurrentPayRateCard tech={tech} />

      {tech.emergency_contacts && tech.emergency_contacts.length > 0 && (
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Emergency Contacts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {tech.emergency_contacts.map((contact) => (
                <div key={contact.id} className="border rounded-lg p-3 space-y-1">
                  <p className="font-medium">{contact.name}</p>
                  <p className="text-sm text-gray-600">{contact.relationship}</p>
                  <p className="text-sm">{contact.phone}</p>
                  {contact.email && <p className="text-sm text-gray-600">{contact.email}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {tech.notes && (
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{tech.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
}) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-gray-400 mt-0.5" />
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm text-gray-900">{value}</p>
      </div>
    </div>
  );
}

const CERT_TYPE_DEFAULTS: Record<string, { cert_name: string; issuing_body: string }> = {
  sprat_1: { cert_name: 'SPRAT Level 1', issuing_body: 'Society of Professional Rope Access Technicians' },
  sprat_2: { cert_name: 'SPRAT Level 2', issuing_body: 'Society of Professional Rope Access Technicians' },
  sprat_3: { cert_name: 'SPRAT Level 3', issuing_body: 'Society of Professional Rope Access Technicians' },
  irata_1: { cert_name: 'IRATA Level 1', issuing_body: 'Industrial Rope Access Trade Association' },
  irata_2: { cert_name: 'IRATA Level 2', issuing_body: 'Industrial Rope Access Trade Association' },
  irata_3: { cert_name: 'IRATA Level 3', issuing_body: 'Industrial Rope Access Trade Association' },
  first_aid: { cert_name: 'First Aid / CPR', issuing_body: 'American Red Cross' },
  cpr: { cert_name: 'CPR / AED', issuing_body: 'American Heart Association' },
  confined_space: { cert_name: 'Confined Space Entry', issuing_body: '' },
  fall_protection: { cert_name: 'Fall Protection Competent Person', issuing_body: '' },
  rigging: { cert_name: 'Rigging Certification', issuing_body: '' },
  osha_10: { cert_name: 'OSHA 10-Hour Construction', issuing_body: 'Occupational Safety and Health Administration' },
  osha_30: { cert_name: 'OSHA 30-Hour Construction', issuing_body: 'Occupational Safety and Health Administration' },
  twic: { cert_name: 'Transportation Worker Identification Credential', issuing_body: 'Transportation Security Administration' },
  custom: { cert_name: '', issuing_body: '' },
};

function CertificationsTab({ techId }: { techId: number }) {
  const { canManageOnboarding: canManageCrew } = usePermissions();
  const { data: certs, isLoading } = useTechCertificationsForTech(techId);
  const [open, setOpen] = useState(false);
  const createMutation = useCreateCertificationForTech();
  const [certForm, setCertForm] = useState({
    cert_type: 'sprat_1' as TechCertType,
    cert_name: CERT_TYPE_DEFAULTS['sprat_1'].cert_name,
    cert_number: '',
    issuing_body: CERT_TYPE_DEFAULTS['sprat_1'].issuing_body,
    issue_date: '',
    expiry_date: '',
    notes: '',
  });
  const [formError, setFormError] = useState<string | null>(null);

  const handleCreate = async () => {
    setFormError(null);
    if (!certForm.issue_date) {
      setFormError('Issue date is required.');
      return;
    }
    try {
      await createMutation.mutateAsync({
        techId,
        data: {
          ...certForm,
          expiry_date: certForm.expiry_date || undefined,
        },
      });
      setOpen(false);
      setCertForm({
        cert_type: 'sprat_1',
        cert_name: CERT_TYPE_DEFAULTS['sprat_1'].cert_name,
        cert_number: '',
        issuing_body: CERT_TYPE_DEFAULTS['sprat_1'].issuing_body,
        issue_date: '',
        expiry_date: '',
        notes: '',
      });
    } catch (err) {
      setFormError(handleApiError(err).message);
    }
  };

  if (isLoading) return <Skeleton className="h-48" />;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Certifications</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          {canManageCrew && (
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-1" />
                Add Certification
              </Button>
            </DialogTrigger>
          )}
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Certification</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {formError && (
                <div className="bg-red-50 text-red-700 p-2 rounded text-sm">{formError}</div>
              )}
              <div>
                <Label>Type</Label>
                <Select
                  value={certForm.cert_type}
                  onValueChange={(v) => {
                    const defaults = CERT_TYPE_DEFAULTS[v] || { cert_name: '', issuing_body: '' };
                    setCertForm((f) => ({
                      ...f,
                      cert_type: v as TechCertType,
                      cert_name: defaults.cert_name,
                      issuing_body: defaults.issuing_body,
                    }));
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sprat_1">SPRAT 1</SelectItem>
                    <SelectItem value="sprat_2">SPRAT 2</SelectItem>
                    <SelectItem value="sprat_3">SPRAT 3</SelectItem>
                    <SelectItem value="irata_1">IRATA 1</SelectItem>
                    <SelectItem value="irata_2">IRATA 2</SelectItem>
                    <SelectItem value="irata_3">IRATA 3</SelectItem>
                    <SelectItem value="first_aid">First Aid</SelectItem>
                    <SelectItem value="cpr">CPR</SelectItem>
                    <SelectItem value="confined_space">Confined Space</SelectItem>
                    <SelectItem value="fall_protection">Fall Protection</SelectItem>
                    <SelectItem value="rigging">Rigging</SelectItem>
                    <SelectItem value="osha_10">OSHA 10</SelectItem>
                    <SelectItem value="osha_30">OSHA 30</SelectItem>
                    <SelectItem value="twic">TWIC</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cert Name</Label>
                <Input
                  value={certForm.cert_name}
                  onChange={(e) => setCertForm((f) => ({ ...f, cert_name: e.target.value }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Cert Number</Label>
                  <Input
                    value={certForm.cert_number}
                    onChange={(e) => setCertForm((f) => ({ ...f, cert_number: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Issuing Body</Label>
                  <Input
                    value={certForm.issuing_body}
                    onChange={(e) => setCertForm((f) => ({ ...f, issuing_body: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Issue Date *</Label>
                  <Input
                    type="date"
                    value={certForm.issue_date}
                    onChange={(e) => setCertForm((f) => ({ ...f, issue_date: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Expiry Date</Label>
                  <Input
                    type="date"
                    value={certForm.expiry_date}
                    onChange={(e) => setCertForm((f) => ({ ...f, expiry_date: e.target.value }))}
                  />
                </div>
              </div>
              <Button onClick={handleCreate} disabled={createMutation.isPending} className="w-full">
                {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Add Certification
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {!certs || certs.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8">No certifications recorded</p>
      ) : (
        <CertificationsTable certs={certs} techId={techId} />
      )}
    </div>
  );
}

function CertificationsTable({ certs, techId }: { certs: TechCertification[]; techId: number }) {
  const { canManageOnboarding: canManageCrew } = usePermissions();
  const updateMutation = useUpdateTechCertification();
  const renewMutation = useRenewTechCertification();
  const uploadMutation = useUploadTechDocument();
  const [editing, setEditing] = useState<TechCertification | null>(null);
  const [scan, setScan] = useState<File | null>(null);
  const [editForm, setEditForm] = useState({
    cert_number: '',
    issuing_body: '',
    issue_date: '',
    expiry_date: '',
    notes: '',
  });
  const [formError, setFormError] = useState<string | null>(null);

  const openEditor = (cert: TechCertification) => {
    setEditing(cert);
    setScan(null);
    setFormError(null);
    setEditForm({
      cert_number: cert.cert_number,
      issuing_body: cert.issuing_body,
      issue_date: cert.issue_date,
      expiry_date: cert.expiry_date ?? '',
      notes: cert.notes,
    });
  };

  const saveCertification = async (renew: boolean) => {
    if (!editing) return;
    setFormError(null);
    if (!editForm.issue_date || (renew && !editForm.expiry_date)) {
      setFormError(renew ? 'Issue date and new expiry date are required.' : 'Issue date is required.');
      return;
    }
    try {
      let document = editing.document ?? undefined;
      if (scan) {
        const data = new FormData();
        data.append('file', scan);
        data.append('name', scan.name);
        data.append('document_type', 'certification');
        if (editForm.expiry_date) data.append('expiry_date', editForm.expiry_date);
        const uploaded = await uploadMutation.mutateAsync({ techId, data });
        document = uploaded.id;
      }
      const data = {
        ...editForm,
        expiry_date: editForm.expiry_date || null,
        document,
      };
      if (renew) {
        await renewMutation.mutateAsync({
          certificationId: editing.id,
          techId,
          data,
        });
      } else {
        await updateMutation.mutateAsync({
          certificationId: editing.id,
          techId,
          data,
        });
      }
      setEditing(null);
      toast.success(renew ? 'Certification renewed' : 'Certification updated');
    } catch (err) {
      setFormError(handleApiError(err).message);
    }
  };

  const isSaving = updateMutation.isPending || renewMutation.isPending || uploadMutation.isPending;

  return (
    <>
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Certification</TableHead>
            <TableHead>Number</TableHead>
            <TableHead>Issue Date</TableHead>
            <TableHead>Expiry Date</TableHead>
            <TableHead>Status</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {certs.map((cert) => (
            <TableRow key={cert.id}>
              <TableCell>
                <div>
                  <p className="font-medium">{cert.cert_name}</p>
                  <p className="text-xs text-gray-500">{cert.issuing_body}</p>
                </div>
              </TableCell>
              <TableCell className="text-gray-600">{cert.cert_number || '-'}</TableCell>
              <TableCell className="text-gray-600">{formatDateOnly(cert.issue_date)}</TableCell>
              <TableCell className="text-gray-600">
                {formatDateOnly(cert.expiry_date, 'No expiry')}
              </TableCell>
              <TableCell>
                <Badge className={certStatusColors[cert.status]}>
                  {cert.status.replace('_', ' ')}
                </Badge>
              </TableCell>
              <TableCell>
                {canManageCrew && (
                  <Button variant="ghost" size="sm" onClick={() => openEditor(cert)}>
                    <Pencil className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
    <Dialog open={editing !== null} onOpenChange={(open) => { if (!open) setEditing(null); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit or renew certification</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {formError && (
            <div className="bg-red-50 text-red-700 p-2 rounded text-sm">{formError}</div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Cert Number</Label>
              <Input
                value={editForm.cert_number}
                onChange={(e) => setEditForm((f) => ({ ...f, cert_number: e.target.value }))}
              />
            </div>
            <div>
              <Label>Issuing Body</Label>
              <Input
                value={editForm.issuing_body}
                onChange={(e) => setEditForm((f) => ({ ...f, issuing_body: e.target.value }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Issue Date *</Label>
              <Input
                type="date"
                value={editForm.issue_date}
                onChange={(e) => setEditForm((f) => ({ ...f, issue_date: e.target.value }))}
              />
            </div>
            <div>
              <Label>Expiry Date</Label>
              <Input
                type="date"
                value={editForm.expiry_date}
                onChange={(e) => setEditForm((f) => ({ ...f, expiry_date: e.target.value }))}
              />
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Input
              value={editForm.notes}
              onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
            />
          </div>
          <div>
            <Label>Certification scan</Label>
            <Input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={(e) => setScan(e.target.files?.[0] ?? null)}
            />
          </div>
          {editing && (editing.renewal_history?.length ?? 0) > 0 && (
            <div>
              <Label>Renewal history</Label>
              <div className="mt-1 space-y-1 text-sm text-gray-600">
                {editing.renewal_history?.map((history) => (
                  <p key={history.id}>
                    {history.issue_date} – {history.expiry_date ?? 'No expiry'}
                    {history.cert_number ? ` · ${history.cert_number}` : ''}
                  </p>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => void saveCertification(false)} disabled={isSaving}>
              Save changes
            </Button>
            <Button onClick={() => void saveCertification(true)} disabled={isSaving}>
              Renew certification
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}

function DocumentsTab({ techId, personId }: { techId: number; personId?: number | null }) {
  const { data: docs, isLoading } = useTechDocuments(techId);
  const uploadMutation = useUploadTechDocument();
  const { data: filedDocuments } = useFiledDocuments(
    personId ? { attached_type: 'person', attached_id: personId, page_size: 100 } : {},
    Boolean(personId)
  );
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name);
      await uploadMutation.mutateAsync({ techId, data: formData });
    } finally {
      setUploading(false);
    }
  };

  if (isLoading) return <Skeleton className="h-48" />;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Documents</h3>
        <Button size="sm" disabled={uploading} asChild>
          <label className="cursor-pointer">
            {uploading ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-1" />
            )}
            Upload Document
            <input type="file" className="hidden" onChange={handleUpload} />
          </label>
        </Button>
      </div>

      {!docs || docs.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8">No documents uploaded</p>
      ) : (
        <DocumentsList docs={docs} />
      )}

      {personId && (
        <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-semibold">Filed Documents</h3>
            <span className="text-xs text-gray-500">Linked to person record #{personId}</span>
          </div>
          {!filedDocuments?.results || filedDocuments.results.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No filed documents linked to this person</p>
          ) : (
            <div className="space-y-2">
              {filedDocuments.results.map((doc: FiledDocument) => (
                <div key={doc.id} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">{doc.title}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      <Badge variant="outline">{doc.category_display}</Badge>
                      {doc.expiry_date ? (
                        doc.days_until_expiry !== null && doc.days_until_expiry < 0 ? (
                          <Badge variant="destructive">Expired</Badge>
                        ) : doc.days_until_expiry !== null && doc.days_until_expiry <= 30 ? (
                          <Badge variant="secondary">Expiring soon</Badge>
                        ) : (
                          <Badge variant="outline">{formatDateOnly(doc.expiry_date)}</Badge>
                        )
                      ) : (
                        <Badge variant="outline">No expiry</Badge>
                      )}
                    </div>
                  </div>
                  {doc.file && (
                    <Button variant="ghost" size="sm" asChild>
                      <a href={resolveMediaUrl(doc.file)} target="_blank" rel="noopener noreferrer">
                        Open file
                      </a>
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DocumentsList({ docs }: { docs: TechDocument[] }) {
  const timeZone = useOrgTimeZone();
  return (
    <div className="space-y-2">
      {docs.map((doc) => (
        <div key={doc.id} className="flex items-center justify-between border rounded-lg p-3">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-gray-400" />
            <div>
              <p className="font-medium text-sm">{doc.name}</p>
              <p className="text-xs text-gray-500">
                {doc.document_type.replace('_', ' ')} &middot; Uploaded{' '}
                {formatDateTimeInTimeZone(doc.created_at, timeZone, { dateStyle: 'medium' })}
              </p>
            </div>
          </div>
          {doc.file && (
            <Button variant="ghost" size="icon" asChild>
              <a href={resolveMediaUrl(doc.file)} target="_blank" rel="noopener noreferrer">
                <Download className="h-4 w-4" />
              </a>
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

function SkillsTab({ techId }: { techId: number }) {
  const { data: skills, isLoading } = useTechSkills(techId);
  const [open, setOpen] = useState(false);
  const createMutation = useCreateTechSkill();
  const [skillForm, setSkillForm] = useState({
    skill_name: '',
    proficiency: 'intermediate' as SkillProficiency,
    notes: '',
  });
  const [formError, setFormError] = useState<string | null>(null);

  const handleCreate = async () => {
    setFormError(null);
    if (!skillForm.skill_name) {
      setFormError('Skill name is required.');
      return;
    }
    try {
      await createMutation.mutateAsync({ techId, data: skillForm });
      setOpen(false);
      setSkillForm({ skill_name: '', proficiency: 'intermediate', notes: '' });
    } catch (err) {
      setFormError(handleApiError(err).message);
    }
  };

  if (isLoading) return <Skeleton className="h-48" />;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Skills</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Add Skill
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Skill</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {formError && (
                <div className="bg-red-50 text-red-700 p-2 rounded text-sm">{formError}</div>
              )}
              <div>
                <Label>Skill Name *</Label>
                <Input
                  value={skillForm.skill_name}
                  onChange={(e) => setSkillForm((f) => ({ ...f, skill_name: e.target.value }))}
                />
              </div>
              <div>
                <Label>Proficiency</Label>
                <Select
                  value={skillForm.proficiency}
                  onValueChange={(v) => setSkillForm((f) => ({ ...f, proficiency: v as SkillProficiency }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                    <SelectItem value="expert">Expert</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleCreate} disabled={createMutation.isPending} className="w-full">
                {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Add Skill
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {!skills || skills.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8">No skills recorded</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {skills.map((skill: TechSkill) => (
            <div key={skill.id} className="border rounded-lg p-3">
              <div className="flex items-center justify-between">
                <p className="font-medium text-sm">{skill.skill_name}</p>
                <Badge className={proficiencyColors[skill.proficiency]}>
                  {skill.proficiency}
                </Badge>
              </div>
              {skill.notes && <p className="text-xs text-gray-500 mt-1">{skill.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AvailabilityTab({ techId }: { techId: number }) {
  const orgToday = useOrgToday();
  const monthEnd = addLocalDays(orgToday, 30);
  const { data: availability, isLoading } = useTechAvailability(techId, orgToday, monthEnd);

  if (isLoading) return <Skeleton className="h-48" />;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Availability (Next 30 Days)</h3>
      </div>

      {!availability || availability.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8">No availability entries</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {availability.map((entry: AvailabilityEntry) => (
            <div key={entry.id} className="border rounded-lg p-2 text-center">
              <p className="text-xs text-gray-500">
                {formatDateOnly(entry.date, '—', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                })}
              </p>
              <Badge className={`mt-1 text-xs ${availabilityColors[entry.status]}`}>
                {entry.status.replace('_', ' ')}
              </Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AssignmentsTab({ techId }: { techId: number }) {
  const { canManageOnboarding: canManageCrew } = usePermissions();
  const { data, isLoading } = useAssignments({ tech: techId, page_size: 50 });
  const navigate = useNavigate();
  const [addTravelOpen, setAddTravelOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<number | null>(null);
  const createJobPacket = useCreateJobPacket();

  if (isLoading) return <Skeleton className="h-48" />;

  const assignments = data?.results ?? [];

  return (
    <div className="space-y-4">
      <h3 className="font-semibold">Assignments</h3>

      {assignments.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8">No assignments</p>
      ) : (
        <div className="space-y-3">
          {assignments.map((assignment: CrewAssignment) => (
            <Card key={assignment.id}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <p className="font-medium">{assignment.project_name}</p>
                    <p className="text-sm text-gray-600">
                      {assignment.role && `${assignment.role} · `}
                      {assignment.location && `${assignment.location} · `}
                      {formatDateOnly(assignment.start_date)} —{' '}
                      {formatDateOnly(assignment.end_date, 'Ongoing')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={assignmentStatusColors[assignment.status]}>
                      {assignment.status}
                    </Badge>
                    {assignment.override_reason && (
                      <OverrideMarker reason={assignment.override_reason} />
                    )}
                    {assignment.travel_itinerary ? (
                      <Badge
                        className={
                          assignment.travel_itinerary.is_confirmed
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-amber-100 text-amber-800'
                        }
                      >
                        {assignment.travel_itinerary.is_confirmed ? 'Travel confirmed' : 'Travel pending'}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs text-gray-500">
                        No itinerary
                      </Badge>
                    )}
                    {canManageCrew && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedAssignment(assignment.id);
                          setAddTravelOpen(true);
                        }}
                      >
                        <Plane className="h-3 w-3 mr-1" />
                        Add Travel
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/crew/travel/itinerary?assignment=${assignment.id}`)}
                    >
                      <FileText className="h-3 w-3 mr-1" />
                      Itinerary
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={createJobPacket.isPending}
                      onClick={async () => {
                        try {
                          const packet = await createJobPacket.mutateAsync({
                            tech: techId,
                            assignment: assignment.id,
                          });
                          navigate(`/crew/job-packets/${packet.id}`);
                        } catch {
                          // mutation error is surfaced by React Query
                        }
                      }}
                    >
                      <Package className="h-3 w-3 mr-1" />
                      Job Packet
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {selectedAssignment !== null && (
        <AddTravelDialog
          assignmentId={selectedAssignment}
          techId={techId}
          open={addTravelOpen}
          onOpenChange={setAddTravelOpen}
        />
      )}
    </div>
  );
}

function TravelTab({ techId }: { techId: number }) {
  const orgToday = useOrgToday();
  const orgCurrency = useOrgCurrency();
  const { data, isLoading } = useTravelRecords({ tech: techId, page_size: 100 });
  const { data: upcomingData } = useUpcomingTravel({ tech: techId, page_size: 50 });

  if (isLoading) return <Skeleton className="h-48" />;

  const records = data?.results ?? [];
  const upcomingRecords = upcomingData?.results ?? [];
  const pastRecords = records.filter((r) => r.departure_date < orgToday);
  const totalCost = records.reduce((sum, r) => sum + (r.cost ? parseFloat(r.cost) : 0), 0);

  return (
    <div className="space-y-6">
      {/* Cost Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Total Records</p>
            <p className="text-2xl font-bold">{records.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Upcoming Travel</p>
            <p className="text-2xl font-bold text-blue-600">{upcomingRecords.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-600">Total Travel Cost</p>
            <p className="text-2xl font-bold text-green-600">
              {formatMoney(totalCost, orgCurrency)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Travel */}
      {upcomingRecords.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold">Upcoming Travel</h3>
          {upcomingRecords.map((record: TravelRecord) => (
            <TravelRecordCard key={record.id} record={record} />
          ))}
        </div>
      )}

      {/* Past Travel */}
      <div className="space-y-3">
        <h3 className="font-semibold">
          {upcomingRecords.length > 0 ? 'Past Travel' : 'Travel Records'}
        </h3>
        {pastRecords.length === 0 && upcomingRecords.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No travel records</p>
        ) : pastRecords.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">No past travel records</p>
        ) : (
          pastRecords.map((record: TravelRecord) => (
            <TravelRecordCard key={record.id} record={record} />
          ))
        )}
      </div>
    </div>
  );
}

function TravelRecordCard({ record }: { record: TravelRecord }) {
  const orgCurrency = useOrgCurrency();

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <Plane className="h-4 w-4 text-gray-400" />
              <p className="font-medium">{record.description}</p>
              <Badge className="text-xs">{travelTypeLabels[record.travel_type]}</Badge>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {formatDateOnly(record.departure_date)}
              {record.return_date && ` — ${formatDateOnly(record.return_date)}`}
            </p>
            {record.confirmation_number && (
              <p className="text-xs text-gray-500 mt-1">
                Confirmation: {record.confirmation_number}
              </p>
            )}
          </div>
          {record.cost && (
            <span className="text-sm font-medium">
              {formatMoney(record.cost, orgCurrency)}
            </span>
          )}
        </div>
        {record.flight_info && (
          <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
            <p>
              {record.flight_info.airline} {record.flight_info.flight_number}:{' '}
              {record.flight_info.departure_airport} &rarr; {record.flight_info.arrival_airport}
            </p>
          </div>
        )}
        {record.car_rental && (
          <div className="mt-2 p-2 bg-green-50 rounded text-sm">
            <p>
              {record.car_rental.rental_company} — {record.car_rental.vehicle_type}
            </p>
            <p className="text-xs text-gray-500">
              {record.car_rental.pickup_location} &rarr; {record.car_rental.dropoff_location}
            </p>
          </div>
        )}
        {record.hotel_booking && (
          <div className="mt-2 p-2 bg-purple-50 rounded text-sm">
            <p className="font-medium">{record.hotel_booking.hotel_name}</p>
            <p className="text-xs text-gray-500">
              {formatDateOnly(record.hotel_booking.check_in_date)} -{' '}
              {formatDateOnly(record.hotel_booking.check_out_date)}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Cross-Module: Projects Tab ──────────────────────────────────────────────

function TechProjectsTab({ techId }: { techId: number }) {
  const { data, isLoading } = useTechProjectsData(techId);

  if (isLoading) return <Skeleton className="h-48" />;

  const projects = data?.project_assignments ?? [];
  const assignments = data?.crew_assignments ?? [];

  return (
    <div className="space-y-6">
      {projects.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3">Current Projects ({projects.length})</h3>
          <div className="space-y-2">
            {projects.map((p: { id: number; name: string; project_number: string; status: string; start_date: string | null; end_date: string | null }) => (
              <Link key={p.id} to={`/projects/${p.id}`} className="block">
                <Card className="hover:bg-gray-50 transition-colors">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{p.name}</p>
                      <p className="text-xs text-gray-500">{p.project_number}</p>
                    </div>
                    <Badge variant="outline" className="capitalize">{p.status}</Badge>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}
      {assignments.length > 0 && (
        <div>
          <h3 className="font-semibold mb-3">Crew Assignments ({assignments.length})</h3>
          <div className="space-y-2">
            {assignments.map((a: { id: number; project_name: string; role: string; status: string; start_date: string }) => (
              <Card key={a.id}>
                <CardContent className="p-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{a.project_name}</p>
                    <p className="text-xs text-gray-500">
                      {a.role} &middot; {formatDateOnly(a.start_date)}
                    </p>
                  </div>
                  <Badge variant="outline" className="capitalize">{a.status}</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
      {projects.length === 0 && assignments.length === 0 && (
        <p className="text-sm text-gray-500 py-8 text-center">No projects found for this technician</p>
      )}
    </div>
  );
}

// ─── Cross-Module: Safety Records Tab ────────────────────────────────────────

function TechSafetyRecordsTab({ techId }: { techId: number }) {
  const timeZone = useOrgTimeZone();
  const { data, isLoading } = useTechSafetyData(techId);

  if (isLoading) return <Skeleton className="h-48" />;

  const jsas = data?.jsa_signatures ?? [];

  return (
    <div className="space-y-4">
      <h3 className="font-semibold">JSAs Signed ({jsas.length})</h3>
      {jsas.length > 0 ? (
        <div className="space-y-2">
          {jsas.map((jsa: { id: number; title: string; status: string; date: string; signed_at: string }) => (
            <Link key={`${jsa.id}-${jsa.signed_at}`} to={`/safety/jsas/${jsa.id}`} className="block">
              <Card className="hover:bg-gray-50 transition-colors">
                <CardContent className="p-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{jsa.title}</p>
                    <p className="text-xs text-gray-500">
                      Date: {formatDateOnly(jsa.date)} &middot; Signed:{' '}
                      {formatDateTimeInTimeZone(jsa.signed_at, timeZone, { dateStyle: 'medium' })}
                    </p>
                  </div>
                  <Badge variant="outline" className="capitalize">{jsa.status}</Badge>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500 py-8 text-center">No safety records found for this technician</p>
      )}
    </div>
  );
}

// ─── Onboarding Tab ──────────────────────────────────────────────────────────

const onboardingItemStatusColors: Record<OnboardingItemStatus, string> = {
  pending: 'bg-gray-100 text-gray-800',
  completed: 'bg-green-100 text-green-800',
  skipped: 'bg-yellow-100 text-yellow-800',
};

const onboardingItemTypeLabels: Record<OnboardingItemType, string> = {
  upload_document: 'Upload Document',
  complete_training: 'Complete Training',
  sign_form: 'Sign Form',
  add_certification: 'Add Certification',
  custom: 'Custom',
};

function OnboardingTab({ techId, tech }: { techId: number; tech: { user: number | null } }) {
  const timeZone = useOrgTimeZone();
  const { user: authUser } = useAuth();
  const { canManageOnboarding, canCompleteOnboarding } = usePermissions();
  const { data: progressList, isLoading } = useTechOnboarding(techId);
  const completeMutation = useCompleteOnboardingItem();
  const skipMutation = useSkipOnboardingItem();
  const canAct =
    authUser?.id === tech.user || canCompleteOnboarding || canManageOnboarding;

  if (isLoading) return <Skeleton className="h-48" />;

  if (!progressList || progressList.length === 0) {
    return (
      <div className="text-center py-12">
        <ListChecks className="h-12 w-12 mx-auto text-gray-300 mb-3" />
        <p className="text-gray-500">No onboarding checklists assigned to this technician.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {progressList.map((progress) => {
        const totalItems = progress.item_progress.length;
        const completedItems = progress.item_progress.filter(
          (ip) => ip.status === 'completed' || ip.status === 'skipped'
        ).length;
        const pct = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

        return (
          <Card key={progress.id}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">{progress.checklist_name}</CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Status: <Badge className={
                      progress.status === 'completed' ? 'bg-green-100 text-green-800' :
                      progress.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }>
                      {progress.status.replace('_', ' ')}
                    </Badge>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold">{pct}%</p>
                  <p className="text-xs text-gray-500">{completedItems}/{totalItems} items</p>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {progress.item_progress.map((ip) => (
                  <div
                    key={ip.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      ip.status === 'completed' ? 'bg-green-50 border-green-200' :
                      ip.status === 'skipped' ? 'bg-yellow-50 border-yellow-200' :
                      'bg-white'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{ip.item_title}</p>
                        {ip.is_required && (
                          <Badge className="bg-red-50 text-red-700 text-xs">Required</Badge>
                        )}
                      </div>
                      {ip.item_description && (
                        <p className="text-xs text-gray-500 mt-0.5">{ip.item_description}</p>
                      )}
                      <Badge variant="outline" className="text-xs mt-1">
                        {onboardingItemTypeLabels[ip.item_type]}
                      </Badge>
                      {ip.completed_at && (
                        <p className="text-xs text-gray-400 mt-1">
                          Completed{' '}
                          {formatDateTimeInTimeZone(ip.completed_at, timeZone, { dateStyle: 'medium' })}
                          {ip.completed_by_username && ` by ${ip.completed_by_username}`}
                        </p>
                      )}
                      {ip.notes && (
                        <p className="text-xs text-gray-500 mt-1 italic">{ip.notes}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={onboardingItemStatusColors[ip.status]}>
                        {ip.status}
                      </Badge>
                      {ip.status === 'pending' && canAct && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-600"
                            onClick={() => completeMutation.mutate({ itemProgressId: ip.id })}
                            disabled={completeMutation.isPending}
                          >
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Complete
                          </Button>
                          {!ip.is_required && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-yellow-600"
                              onClick={() => skipMutation.mutate({ itemProgressId: ip.id })}
                              disabled={skipMutation.isPending}
                            >
                              <SkipForward className="h-3 w-3 mr-1" />
                              Skip
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// ─── Performance Tab ─────────────────────────────────────────────────────────

function PerformanceTab({ techId }: { techId: number }) {
  const { canManageOnboarding: canManageCrew } = usePermissions();
  const orgToday = useOrgToday();
  const { data, isLoading } = usePerformanceSummary(techId);
  const createReview = useCreatePerformanceReview();
  const [showForm, setShowForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    rating: 3,
    review_date: orgToday,
    period_start: '',
    period_end: '',
    strengths: '',
    areas_for_improvement: '',
    notes: '',
  });
  const [formError, setFormError] = useState<string | null>(null);

  const handleCreate = async () => {
    setFormError(null);
    if (!reviewForm.period_start || !reviewForm.period_end) {
      setFormError('Period start and end dates are required.');
      return;
    }
    try {
      await createReview.mutateAsync({
        tech: techId,
        ...reviewForm,
      });
      setShowForm(false);
      setReviewForm({
        rating: 3,
        review_date: orgToday,
        period_start: '',
        period_end: '',
        strengths: '',
        areas_for_improvement: '',
        notes: '',
      });
    } catch (err) {
      setFormError(handleApiError(err).message);
    }
  };

  if (isLoading) return <Skeleton className="h-48" />;

  const metrics = data?.metrics;
  const reviews = data?.reviews ?? [];

  return (
    <div className="space-y-6">
      {metrics && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-gray-600">Cert Compliance</p>
              <p className="text-2xl font-bold mt-1">{metrics.cert_compliance_pct}%</p>
              <p className="text-xs text-gray-500">{metrics.current_certs}/{metrics.total_certs} current</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-gray-600">Assignments Completed</p>
              <p className="text-2xl font-bold mt-1">{metrics.assignments_completed}</p>
              <p className="text-xs text-gray-500">of {metrics.assignments_total} total</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-gray-600">Reviews</p>
              <p className="text-2xl font-bold mt-1">{reviews.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-sm text-gray-600">Avg Rating</p>
              <p className="text-2xl font-bold mt-1">
                {reviews.length > 0
                  ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
                  : '-'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Performance Reviews</h3>
        <Dialog open={showForm} onOpenChange={setShowForm}>
          {canManageCrew && (
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4 mr-1" />
                Add Review
              </Button>
            </DialogTrigger>
          )}
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>New Performance Review</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {formError && <div className="bg-red-50 text-red-700 p-2 rounded text-sm">{formError}</div>}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Review Date</Label>
                  <Input
                    type="date"
                    value={reviewForm.review_date}
                    onChange={(e) => setReviewForm((f) => ({ ...f, review_date: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Period Start *</Label>
                  <Input
                    type="date"
                    value={reviewForm.period_start}
                    onChange={(e) => setReviewForm((f) => ({ ...f, period_start: e.target.value }))}
                  />
                </div>
                <div>
                  <Label>Period End *</Label>
                  <Input
                    type="date"
                    value={reviewForm.period_end}
                    onChange={(e) => setReviewForm((f) => ({ ...f, period_end: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <Label>Rating (1-5)</Label>
                <div className="flex gap-1 mt-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      onClick={() => setReviewForm((f) => ({ ...f, rating: n }))}
                      className="p-1"
                    >
                      <Star
                        className={`h-6 w-6 ${
                          n <= reviewForm.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Strengths</Label>
                <textarea
                  className="w-full border rounded-md p-2 text-sm min-h-[60px]"
                  value={reviewForm.strengths}
                  onChange={(e) => setReviewForm((f) => ({ ...f, strengths: e.target.value }))}
                />
              </div>
              <div>
                <Label>Areas for Improvement</Label>
                <textarea
                  className="w-full border rounded-md p-2 text-sm min-h-[60px]"
                  value={reviewForm.areas_for_improvement}
                  onChange={(e) => setReviewForm((f) => ({ ...f, areas_for_improvement: e.target.value }))}
                />
              </div>
              <div>
                <Label>Notes</Label>
                <textarea
                  className="w-full border rounded-md p-2 text-sm min-h-[60px]"
                  value={reviewForm.notes}
                  onChange={(e) => setReviewForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </div>
              <Button onClick={handleCreate} disabled={createReview.isPending} className="w-full">
                {createReview.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Submit Review
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {reviews.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">No performance reviews yet.</p>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <Card key={r.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">
                        {formatDateOnly(r.period_start)} — {formatDateOnly(r.period_end)}
                      </p>
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star
                            key={n}
                            className={`h-4 w-4 ${
                              n <= r.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Reviewed by {r.reviewer_username} on {formatDateOnly(r.review_date)}
                    </p>
                  </div>
                </div>
                {r.strengths && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-green-700">Strengths</p>
                    <p className="text-sm text-gray-700">{r.strengths}</p>
                  </div>
                )}
                {r.areas_for_improvement && (
                  <div className="mt-2">
                    <p className="text-xs font-medium text-orange-700">Areas for Improvement</p>
                    <p className="text-sm text-gray-700">{r.areas_for_improvement}</p>
                  </div>
                )}
                {r.notes && (
                  <div className="mt-2">
                    <p className="text-xs font-medium text-gray-500">Notes</p>
                    <p className="text-sm text-gray-700">{r.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
